import { z } from "zod";

import { VibeLogPipelineHealthSchema } from "./log-pipeline";

/** One log line, as the gateway renders it. */
export const VibeLogLineSchema = z.object({
  /** Loki's own nanosecond epoch timestamp, verbatim. Doubles as the paging cursor. */
  timestampNs: z.string(),
  /** The same instant as ISO 8601, so a caller never has to do nanosecond arithmetic. */
  timestamp: z.string(),
  /** The log line itself, exactly as the app emitted it. */
  message: z.string(),
  /** The deployment slot this line came from, when the record carries one. */
  color: z.string().nullable()
});

export type VibeLogLine = z.infer<typeof VibeLogLineSchema>;

export const VibeLogPageSchema = z.object({
  /** Newest first. At most `limit` entries. */
  lines: z.array(VibeLogLineSchema),
  /**
   * Pass as the next request's `cursor` to page further back, or null when this
   * page reached the start of the window.
   *
   * Null on a SHORT page rather than on an empty one: a full page means more may
   * exist, a short page means the window is exhausted. Deriving it from the page
   * size rather than from Loki's response is what keeps paging terminating.
   */
  nextCursor: z.string().nullable(),
  /**
   * Whether the pipeline that would have delivered these lines is working,
   * assessed by the gateway at read time — see `assessLogPipeline`.
   *
   * 🔴 THIS IS WHAT SEPARATES "NO LOGS IN THIS WINDOW" FROM "NOTHING CAN ARRIVE".
   * Both are `lines: []`, and before this field they were the same bytes: every
   * tenant's log store stayed empty for months while every read answered an
   * honest-looking empty page.
   *
   * Optional ONLY because a gateway bundle older than this field sends none. An
   * absent verdict is read as `unverified` by every consumer, never as healthy.
   */
  pipeline: VibeLogPipelineHealthSchema.optional()
});

export type VibeLogPage = z.infer<typeof VibeLogPageSchema>;

/**
 * The NAMED SSE events the follow route emits, and the payload of the one that
 * carries content.
 *
 * ⚠️ THIS IS THE TENANT-FACING WIRE, NOT THE CONSOLE-FACING ONE. The gateway
 * writes `event: lines` / `event: error` with an `id:` resume point
 * (`write-sse.ts`), because its client is an `EventSource` INSIDE the tenant
 * edge that reconnects and echoes `Last-Event-ID`. The control plane parses this
 * and re-emits `VibeAppLogStreamEventSchema` — data-only frames, no `id:` —
 * because a browser cannot address a resume point inside a tenant. Two hops, two
 * formats, on purpose.
 *
 * Single-sourced here for the same reason the request schemas are: the daemon
 * that writes these frames and the control plane that reads them sit on opposite
 * sides of a trust boundary and cannot see each other's code. A hand-rolled
 * parse on either side is a second definition of one wire, free to drift from
 * the thing actually producing it.
 */
export const VIBE_LOG_GATEWAY_STREAM_EVENT_LINES = "lines";
export const VIBE_LOG_GATEWAY_STREAM_EVENT_ERROR = "error";

/**
 * A `lines` frame's payload.
 *
 * `.strict()` so a field the gateway starts sending is a visible parse failure
 * at the control plane rather than a silently dropped half of a contract change.
 */
export const VibeLogStreamLinesFrameSchema = z
  .object({
    /** Oldest first within the frame — a follow moves forward, unlike a page. */
    lines: z.array(VibeLogLineSchema)
  })
  .strict();

export type VibeLogStreamLinesFrame = z.infer<typeof VibeLogStreamLinesFrameSchema>;
